/**
 * Handicraft/Souvenir Item Component
 * Displays individual handicraft/souvenir with image and description
 * Follows Single Responsibility Principle - only responsible for rendering a single item
 */

import React from 'react';
import {
  spacing,
  borders,
  typography,
  typographyColors,
  strokeColors,
  backgroundColors,
} from '@/design-system/utils';
import { ImageThumbnail } from '@/components/ui/image-thumbnail';
import { ExpandableText } from '@/components/ui/expandable-text';
import { Heading } from '@/components/ui/heading';
import { HandicraftSouvenir } from './types';

export interface HandicraftItemProps {
  /**
   * Handicraft/souvenir data
   */
  item: HandicraftSouvenir;
  /**
   * Place name for image fetching context
   */
  placeName?: string;
  /**
   * Whether to use mobile layout
   */
  isMobile?: boolean;
}

/**
 * Handicraft/Souvenir Item Component
 * Renders a single handicraft/souvenir with image, title, and description
 * Matches Figma design: horizontal layout with thumbnail on left, text on right
 * Uses the same design as LocalExperienceItem
 */
export const HandicraftItem: React.FC<HandicraftItemProps> = ({
  item,
  placeName,
}) => {
  const thumbnailWidth = 84;
  const thumbnailHeight = 72;

  return (
    <div
      style={{
        display: 'flex',
        padding: spacing[3],
        alignItems: 'flex-start',
        gap: spacing[3],
        alignSelf: 'stretch',
        borderRadius: borders.radius.xl,
        border: `1px solid ${strokeColors.lightest}`,
        backgroundColor: backgroundColors.primary,
      }}
    >
      {/* Thumbnail Image - 84px x 72px */}
      <div
        style={{
          position: 'relative',
          width: thumbnailWidth,
          height: thumbnailHeight,
          borderRadius: borders.radius.md,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <ImageThumbnail
          image={item.image}
          alt={item.title}
          title={item.title}
          description={item.description}
          placeName={placeName}
          category='Handicrafts and Souvenirs'
          width={`${thumbnailWidth}px`}
          height={`${thumbnailHeight}px`}
          onImageLoad={() => {
            console.log('✅ [HandicraftItem] Image loaded in DOM:', {
              itemTitle: item.title,
              itemId: item.id,
            });
          }}
        />
      </div>

      {/* Content - Title, Description */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[1],
          flex: 1,
          minWidth: 0, // Allow text to wrap
        }}
      >
        {/* Title */}
        <Heading
          as='h4'
          fontSize={typography.size.base}
          fontWeight={typography.weight.semibold}
          color={typographyColors.text}
          lineHeight={typography.lineHeight.normal}
        >
          {item.title}
        </Heading>

        {/* Description */}
        {item.description && (
          <ExpandableText
            text={item.description}
            maxLines={2}
            fontSize={typography.size.sm}
            fontWeight={typography.weight.regular}
            color={typographyColors.text}
            lineHeight={typography.lineHeight.relaxed}
          />
        )}
      </div>
    </div>
  );
};
